import {
  Autocomplete,
  AutocompleteOption,
  Button,
  Card,
  Chip,
  Divider,
  FormControl,
  FormHelperText,
  FormLabel,
  IconButton,
  Icons,
  Input,
  Stack,
  ToggleButtonGroup,
  Typography,
} from '@healthinal/ui';
import { Fragment, ReactNode, useEffect } from 'react';
import {
  Control,
  Controller,
  FieldArrayPath,
  FieldPath,
  FieldValues,
  useFieldArray,
  useFormContext,
} from 'react-hook-form';
import {
  ClinicalReportDtoOrgansItem,
  ClinicalReportMaterialDtoClinicalTestMaterial,
  ClinicalReportMaterialResultDtoClinicalDetectionMethod,
  ClinicalReportMaterialResultDtoTestResult,
} from '../../../../../api/generated/model';
import { FileInput } from '../../../../../components/form/FileInput.tsx';
import { theme } from '../../../../../theme.ts';
import {
  getClinicalDetectionMethodName,
  getClinicalTestMaterialName,
  getOrganName,
} from '../../../../../utils/clinicalReport.ts';
import { getTestResultName } from '../../../../../utils/labReport.tsx';

interface FormValuesWithMaterials extends FieldValues {
  materials: {
    clinicalTestMaterial: ClinicalReportMaterialDtoClinicalTestMaterial;
    otherMaterial: string | null;
    materialResults: {
      clinicalDetectionMethod: ClinicalReportMaterialResultDtoClinicalDetectionMethod;
      testResult: ClinicalReportMaterialResultDtoTestResult | null;
    }[];
  }[];
}

interface ClinicalReportDiagnosisFormProps {
  affectedOrgans: FieldPath<FormValuesWithMaterials>;
  otherOrgan: FieldPath<FormValuesWithMaterials>;
  file: FieldPath<FormValuesWithMaterials>;
  materials: FieldArrayPath<FormValuesWithMaterials>;
  fileInputPlaceHolder?: string;
  children?: ReactNode;
}

export function ClinicalReportDiagnosisForm({
  affectedOrgans,
  otherOrgan,
  file,
  materials,
  fileInputPlaceHolder = 'MKB PDF',
  children,
}: ClinicalReportDiagnosisFormProps) {
  const {
    register,
    control,
    setValue,
    watch,
    clearErrors,
    formState: { errors },
  } = useFormContext<FormValuesWithMaterials>();

  const { fields: materialFields } = useFieldArray({ control, name: materials });
  const materialValues = watch(materials) as FormValuesWithMaterials[];

  const organValues = watch(affectedOrgans) as ClinicalReportDtoOrgansItem[];

  const otherOrganEnabled = organValues.includes(ClinicalReportDtoOrgansItem.OTHER);

  useEffect(() => {
    if (!otherOrganEnabled) {
      setValue(otherOrgan, '');
      clearErrors(otherOrgan);
    }
  }, [otherOrganEnabled]);

  return (
    <Stack gap={2}>
      <Typography level={'title-lg'}>Diagnose und Manifestation</Typography>
      <Stack direction={'row'} justifyContent={'space-between'} gap={2}>
        <FormControl sx={{ flex: 2 }}>
          <FormLabel>
            <Typography>Befallene Organe</Typography>
          </FormLabel>
          <Controller
            name={affectedOrgans}
            control={control}
            render={({ field: { onChange, value } }) => (
              <Autocomplete
                multiple
                value={value as ClinicalReportDtoOrgansItem[]}
                options={Object.values(ClinicalReportDtoOrgansItem).map((item) => item)}
                getOptionLabel={(option) => option}
                onChange={(_, item) => onChange(item)}
                filterSelectedOptions
                getOptionKey={(option) => option}
                isOptionEqualToValue={(option, value) => option === value}
                renderOption={(props, option) => (
                  <AutocompleteOption {...props} key={option}>
                    {getOrganName(option)}
                  </AutocompleteOption>
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip {...getTagProps({ index })} key={option} endDecorator={<Icons.Close />}>
                      {getOrganName(option)}
                    </Chip>
                  ))
                }
              />
            )}
          />
        </FormControl>
        {otherOrganEnabled && (
          <FormControl sx={{ flex: 1 }} error={!!errors[otherOrgan]}>
            <FormLabel>
              <Typography>Organ *</Typography>
            </FormLabel>
            <Input {...register(otherOrgan, { required: otherOrganEnabled })} />
            {errors[otherOrgan] && <FormHelperText>Organ muss ausgefüllt werden</FormHelperText>}
          </FormControl>
        )}
      </Stack>
      <Stack direction={'row'} justifyContent={'space-between'} gap={2}>
        <FormControl sx={{ flex: 1 }}>
          <FormLabel>
            <Typography>MKB PDF</Typography>
          </FormLabel>
          <Controller
            name={file}
            control={control}
            render={({ field: { onChange, value } }) => (
              <FileInput
                value={value as File}
                onChange={onChange}
                accept="application/pdf"
                placeholder={fileInputPlaceHolder}
              />
            )}
          />
        </FormControl>
      </Stack>
      <Divider inset={'context'} />
      <Typography level={'title-lg'}>Material</Typography>
      {materialFields.map((materialField, index) => (
        <Fragment key={materialField.id}>
          <Card variant={'outlined'} sx={{ flex: 1, bgcolor: theme.vars.palette.background.surface }}>
            <Typography level={'title-md'}>
              {materialValues[index]?.clinicalTestMaterial
                ? getClinicalTestMaterialName(
                    materialValues[index]?.clinicalTestMaterial as ClinicalReportMaterialDtoClinicalTestMaterial,
                  )
                : 'Unknown Material'}
            </Typography>
            {materialValues[index]?.clinicalTestMaterial === ClinicalReportMaterialDtoClinicalTestMaterial.OTHER && (
              <Stack direction={'row'}>
                <FormLabel sx={{ flex: 1 }}>Beschreibung</FormLabel>
                <Input
                  sx={{ flex: 5 }}
                  {...register(`materials.${index}.otherMaterial`)}
                  placeholder={'Anderes Material spezifizieren'}
                />
              </Stack>
            )}
            <SelectMaterialResult
              index={index}
              control={control}
              disabled={
                materialValues[index]?.clinicalTestMaterial === ClinicalReportMaterialDtoClinicalTestMaterial.OTHER &&
                !materialValues[index].otherMaterial
              }
            />
          </Card>
        </Fragment>
      ))}
      {children}
    </Stack>
  );
}

function SelectMaterialResult({
  index,
  control,
  disabled,
}: {
  index: number;
  control: Control<FormValuesWithMaterials>;
  disabled: boolean;
}) {
  const { fields: resultFields, update: updateResult } = useFieldArray({
    control,
    name: `materials.${index}.materialResults`,
  });

  return (
    <>
      {resultFields.map((resultField, index) => (
        <Fragment key={resultField.id}>
          <Stack direction={'row'}>
            <FormLabel sx={{ flex: 1 }}>
              {getClinicalDetectionMethodName(resultField.clinicalDetectionMethod)}
            </FormLabel>
            <ToggleButtonGroup disabled={disabled} sx={{ flex: 5 }} value={resultField.testResult}>
              {Object.values(ClinicalReportMaterialResultDtoTestResult).map((result) => (
                <Button
                  value={result}
                  key={result}
                  onClick={() =>
                    updateResult(index, {
                      clinicalDetectionMethod: resultField.clinicalDetectionMethod,
                      testResult: result,
                    })
                  }>
                  {getTestResultName(result)}
                </Button>
              ))}
              <IconButton
                disabled={!resultField.testResult}
                onClick={() =>
                  updateResult(index, {
                    clinicalDetectionMethod: resultField.clinicalDetectionMethod,
                    testResult: null,
                  })
                }>
                <Icons.HighlightOff />
              </IconButton>
            </ToggleButtonGroup>
          </Stack>
        </Fragment>
      ))}
    </>
  );
}
